import React, { useState, useEffect, useRef } from "react";
import { Form, Label, Checkbox, Segment } from "semantic-ui-react";
import PropTypes from "prop-types";
import { isEmpty, get, isEqual } from "lodash";
import Joi from "joi";
import Moment from "moment";

import { connect } from "react-redux";
import genericAction from "@Actions/generic";

import IKModal from "@Components/IkModal";
import Input from "@Components/Input";
import ConfirmModal from "@Modals/ConfirmModal";
import CustomDropdown from "./Components/CustomDropdown";
import styles from "./styles.module.css";

const EVENT_VALUES = ["chat.closed", "chat.create", "message.create", "whatsapp.receive", "chat.closed.media"];

// const eventOptions = EVENT_VALUES.map((event) => {return {
// 	key: event,
// 	value: event,
// 	text: event,
// }});

const WebhookCrud = ({ onClose, data, modalOption, dispatch }) => {
	const [endpointUrl, setEndpointUrl] = useState("");
	const [apiKey, setApiKey] = useState("");
	const [events, setEvents] = useState([]);
	const [isActive, setIsActive] = useState(true);
	const [userCreatedId, setUserCreatedId] = useState("");
	const [createdDate, setCreatedDate] = useState("");

	const [errors, setErrors] = useState({
		endpointUrl: "",
		apiKey: "",
		events: "",
	});

	const itemEditWebhook = get(data, "webhook", {});
	const initialMode = modalOption || (isEmpty(itemEditWebhook) ? "Add" : "Watch");
	const [mode, setMode] = useState(initialMode);
	const hasLoadedData = useRef(false);
	const currentWebhookId = useRef(null);
	const [open, setOpen] = useState(false);
	const [initialFormData, setInitialFormData] = useState({});

	useEffect(() => {
		const newMode = modalOption || (isEmpty(itemEditWebhook) ? "Add" : "Watch");
		setMode(newMode);
	}, [modalOption, itemEditWebhook]);

	const schema = Joi.object({
		endpointUrl: Joi.string().uri().required().messages({
			"string.empty": "URL del Endpoint es requerido",
			"string.uri": "Debe ser una URI válida (ej. https://dominio.com)",
		}),
		apiKey: Joi.string().required().messages({
			"string.empty": "API Key es requerida",
		}),
		events: Joi.array()
			.items(Joi.string().valid(...EVENT_VALUES))
			.min(1)
			.required()
			.messages({
				"array.min": "Debes seleccionar al menos un evento",
				"any.required": "Debes seleccionar al menos un evento",
				"any.only": `Evento inválido. Solo se permiten: ${EVENT_VALUES.join(", ")}`,
			}),
	});

	useEffect(() => {
		const webhookIdFromData = get(itemEditWebhook, "id");

		if (currentWebhookId.current !== webhookIdFromData || !hasLoadedData.current) {
			hasLoadedData.current = false;
			currentWebhookId.current = webhookIdFromData;
		}

		(async () => {
			if ((mode === "Edit" || mode === "Watch") && webhookIdFromData && !hasLoadedData.current) {
				hasLoadedData.current = true;
				const webhookData = await dispatch(
					genericAction({
						method: "get",
						route: `webhooks/${webhookIdFromData}`,
					})
				);

				if (!isEmpty(webhookData)) {
					const formData = {
						endpointUrl: get(webhookData, "endpointUrl", ""),
						apiKey: get(webhookData, "jwtToken", ""),
						events: get(webhookData, "events", []) || [],
						isActive: get(webhookData, "isActive", true),
						userCreatedId: get(webhookData, "createdUser.fullName", ""),
						createdDate: get(webhookData, "createdDate", ""),
					};

					setEndpointUrl(formData.endpointUrl);
					setApiKey(formData.apiKey);
					setEvents(formData.events);
					setIsActive(formData.isActive);
					setUserCreatedId(formData.userCreatedId);
					setCreatedDate(formData.createdDate);
					setInitialFormData(formData);
				}
			} else if (mode === "Add") {
				hasLoadedData.current = true;
				const emptyFormData = {
					endpointUrl: "",
					apiKey: "",
					events: [],
					isActive: true,
					userCreatedId: "",
					createdDate: "",
				};
				setEndpointUrl(emptyFormData.endpointUrl);
				setApiKey(emptyFormData.apiKey);
				setEvents(emptyFormData.events);
				setIsActive(emptyFormData.isActive);
				setUserCreatedId(emptyFormData.userCreatedId);
				setCreatedDate(emptyFormData.createdDate);
				setInitialFormData(emptyFormData);
			}
		})();
	}, [mode, itemEditWebhook]);

	const handleSaveWebhook = async () => {
		const validation = schema.validate({ endpointUrl, apiKey, events }, { abortEarly: false });
		if (validation.error) {
			const errorsObj = validation.error.details.reduce(
				(acc, item) => {
					acc[item.path[0]] = item.message;
					return acc;
				},
				{ endpointUrl: "", apiKey: "", events: "" }
			);
			setErrors(errorsObj);
			return;
		}

		const payloadData = {
			endpointUrl,
			jwtToken: apiKey,
			events,
			isActive,
		};

		if (mode === "Add") {
			const dataAdd = await dispatch(
				genericAction({
					method: "post",
					route: "webhooks",
					body: payloadData,
				})
			);
			if (!isEmpty(dataAdd)) {
				const emptyFormData = {
					endpointUrl: "",
					apiKey: "",
					events: [],
					isActive: true,
					userCreatedId: "",
					createdDate: "",
				};
				setInitialFormData(emptyFormData);
				onClose(true);
			}
		} else if (mode === "Edit") {
			const editWebhookId = get(itemEditWebhook, "id", "");
			const dataEdit = await dispatch(
				genericAction({
					method: "patch",
					route: `webhooks/${editWebhookId}`,
					body: payloadData,
				})
			);
			if (!isEmpty(dataEdit)) {
				const currentFormData = {
					endpointUrl,
					apiKey,
					events,
					isActive,
					userCreatedId,
					createdDate,
				};
				setInitialFormData(currentFormData);
				onClose(true);
			}
		}
	};

	const cancelModal = () => {
		onClose();
	};

	const modalOpen = () => {
		const currentFormData = {
			endpointUrl,
			apiKey,
			events,
			isActive,
			userCreatedId,
			createdDate,
		};

		if (mode === "Add") {
			const isFormEmpty = !endpointUrl && !apiKey && events.length === 0;

			if (isFormEmpty) {
				cancelModal();
			} else if (open) {
				setOpen(false);
			} else {
				setOpen(true);
			}
		} else if (isEqual(initialFormData, currentFormData)) {
			cancelModal();
		} else if (open) {
			setOpen(false);
		} else {
			setOpen(true);
		}
	};

	const handleEventsChange = (e, { value }) => {
		setEvents(value);
		setErrors({ ...errors, events: "" });
	};

	return (
		<>
			<IKModal
				title={mode === "Add" ? "Configuración de Webhook" : `Webhook Id: ${get(itemEditWebhook, "id", "")}`}
				mode={mode}
				setMode={setMode}
				size="tiny"
				onClose={modalOpen}
				onSuccess={handleSaveWebhook}
				ignoreContent>
				<div className={mode === "Add" ? styles.formContainer : styles.formContainerEdit}>
					<Form>
						<Form.Field className={styles.fieldRow}>
							<span className={styles.span}>Activo:</span>
							<div>
								<Checkbox
									toggle
									label={isActive ? "Sí" : "No"}
									checked={isActive}
									onChange={(e, { checked }) => {
										return setIsActive(checked);
									}}
									disabled={mode === "Watch"}
								/>
							</div>
						</Form.Field>

						<Input
							label="URL del Endpoint:"
							icon="linkify"
							placeholder="https://midominio.com/webhook"
							name="endpointUrl"
							value={endpointUrl}
							required
							disabled={mode === "Watch"}
							onChange={(e) => {
								setEndpointUrl(e.target.value);
								setErrors({ ...errors, endpointUrl: "" });
							}}
							error={!isEmpty(errors.endpointUrl)}
						/>
						{!isEmpty(errors.endpointUrl) && (
							<Label basic color="red" pointing="above" prompt className={styles.errorLabel}>
								{errors.endpointUrl}
							</Label>
						)}

						<Input
							label="API Key:"
							icon="key"
							placeholder="Ey..."
							name="apiKey"
							value={apiKey}
							required
							disabled={mode === "Watch"}
							onChange={(e) => {
								setApiKey(e.target.value);
								setErrors({ ...errors, apiKey: "" });
							}}
							error={!isEmpty(errors.apiKey)}
						/>
						{!isEmpty(errors.apiKey) && (
							<Label basic color="red" pointing="above" prompt className={styles.errorLabel}>
								{errors.apiKey}
							</Label>
						)}

						<Form.Field className={styles.fieldRow} required>
							<span className={styles.span}>
								Eventos: <span className={styles.requiredAsterisk}>*</span>
							</span>
							<div style={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
								<div style={{ flex: 1 }}>
									<CustomDropdown
										options={EVENT_VALUES.map((event) => {
											return {
												key: event,
												value: event,
												text: event,
											};
										})}
										value={events}
										// upward
										onChange={handleEventsChange}
										placeholder="Seleccionar Evento..."
										disabled={mode === "Watch"}
										error={!isEmpty(errors.events)}
									/>
									{!isEmpty(errors.events) && (
										<Label basic color="red" pointing prompt className={styles.errorLabel}>
											{errors.events}
										</Label>
									)}
								</div>
							</div>
						</Form.Field>
					</Form>
				</div>

				{modalOption !== "Add" && (
					<Segment color="blue" className={styles.infoSegment}>
						<div className={styles.infoBlock}>
							{userCreatedId && (
								<div>
									<strong className={styles.span}>Creador por:</strong> {userCreatedId}
								</div>
							)}
							{createdDate && (
								<div>
									<strong className={styles.span}>Fecha creación:</strong> {Moment(createdDate).format("DD/MM/YYYY")}
								</div>
							)}
						</div>
					</Segment>
				)}
			</IKModal>

			{open && (
				<ConfirmModal
					visible
					icon="exclamation circle"
					header="Confirmar Salir"
					buttonLabel="Salir"
					fullContent="Tienes cambios sin guardar. ¿Estás seguro que deseas salir?"
					onConfirm={() => {
						setOpen(false);
						cancelModal();
					}}
					onDismiss={() => {
						setOpen(false);
					}}
				/>
			)}
		</>
	);
};

WebhookCrud.propTypes = {
	dispatch: PropTypes.func.isRequired,
	onClose: PropTypes.func.isRequired,
	data: PropTypes.object,
	modalOption: PropTypes.string,
};

export default connect()(WebhookCrud);
